import React from 'react';
import { Link } from 'react-router-dom';
import { X, ShoppingBag } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useRegion } from '../../context/RegionContext';
import CartItem from './CartItem';
import Button from '../common/Button';

const Cart = () => {
  const { cartItems, isCartOpen, setIsCartOpen, getCartTotal, getCartCount, clearCart } = useCart();
  const { region, getCurrencySymbol } = useRegion();

  const total = getCartTotal(region);

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in"
        onClick={() => setIsCartOpen(false)}
      />

      {/* Cart Panel */}
      <div className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-white shadow-2xl z-50 animate-slide-in flex flex-col" role="dialog" aria-modal="true" aria-label="Shopping cart">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-6 h-6 text-primary-700" />
            <h2 className="text-2xl font-display font-bold text-dark-800">
              Shopping Cart
            </h2>
            {cartItems.length > 0 && (
              <span className="px-2 py-1 bg-primary-600 text-white text-sm font-bold rounded-full">
                {getCartCount()}
              </span>
            )}
          </div>
          <button
            onClick={() => setIsCartOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-300"
          >
            <X className="w-6 h-6 text-dark-700" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag className="w-24 h-24 text-gray-300 mb-4" />
              <h3 className="text-xl font-display font-bold text-dark-800 mb-2">
                Your cart is empty
              </h3>
              <p className="text-gray-600 mb-6">
                Start shopping to add items to your cart
              </p>
              <Link to="/shop">
               <Button onClick={() => setIsCartOpen(false)}>
                Continue Shopping
              </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => (
                <CartItem key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>

        {/* Footer with Total and Checkout */}
        {cartItems.length > 0 && (
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            {/* Summary */}
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal</span>
                <span className="font-semibold">
                  {getCurrencySymbol()}{total.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Delivery</span>
                <span>Calculated at checkout</span>
              </div>
              <div className="pt-3 border-t border-gray-300 flex justify-between text-lg font-bold text-dark-800">
                <span>Total</span>
                <span className="text-primary-700">
                  {getCurrencySymbol()}{total.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
             <Link to="/checkout">
              <Button className="w-full" size="lg" onClick={() => setIsCartOpen(false)}>
                Continue to Secure Checkout
              </Button>
              </Link>
              <button
                onClick={clearCart}
                className="w-full px-6 py-3 text-red-600 border-2 border-red-300 rounded-lg hover:bg-red-50 transition-all duration-300 font-medium"
              >
                Clear Cart
              </button>
            </div>

            {/* Security Badge */}
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Secure checkout — taxes and delivery confirmed next
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Cart;
